import React, { useState } from 'react';
import Localize from 'react-intl-universal';

import { get } from 'lodash-es';

//MUI
import Search from '@mui/icons-material/Search';
import ViewList from '@mui/icons-material/ViewList';
import ViewModule from '@mui/icons-material/ViewModule';
import { alpha } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Drawer from '@mui/material/Drawer';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import EventItem from '@components/EventItem/EventItem';
import Sorters from '@components/Sorters/Sorters';
import { useGlobalContext } from '@hooks/Context/GlobalContext';
import CatalogFilters from '@pages/Catalog/CatalogFilters';
import { customerLoyaltySorters } from '@pages/Catalog/config';

import FilterButton from './FilterButton';
import useCatalogLogic from './hooks/useCatalogLogic';

const StyledBox = styled(Box)(({ theme }) => ({
  padding: '2rem',
  background: theme.palette.warning.light,
  borderRadius: '5px'
}));
const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: 400,
  '& .MuiPaper-root.MuiDrawer-paper': {
    backgroundColor: theme?.styleOverrides?.Drawer?.backgroundColor,
    backgroundImage: 'none'
  }
}));
const StyledInputBase = styled(InputBase)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[300]}`,
  padding: '0 8px',
  marginLeft: 1,
  borderRadius: '4px'
}));

const StyleViewList = styled(ViewList)(({ theme }) => ({
  color: theme.palette.primary.contrastText
}));
const StyledViewModule = styled(ViewModule)(({ theme }) => ({
  color: theme.palette.primary.contrastText
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  '&:hover': {
    backgroundColor: alpha(get(theme.palette.primary, 'main'), 0.4)
  }
}));

const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  marginTop: '-2.92rem',
  paddingTop: '2.92rem',
  paddingBottom: '2.5rem',
  marginBottom: '-2.5rem',
  background: theme.palette.background.primary
}));

const Catalog = ({ isOwnEvents = false, configuration }) => {
  const [contextData] = useGlobalContext();

  const isInfinityList = isOwnEvents
    ? configuration?.global?.mainCustomerEventsInfinityScroll
    : configuration?.global?.catalogInfinityScroll;

  const {
    isLoading,
    onApplyFilters,
    onResetFilters,
    onSearchFieldChange,
    events = [],
    totalPages,
    currentPage,
    setCurrentPage,
    availableFilterOptions,
    handleSorting,
    searchParams,
    onItemClick,
    onFilterChange,
    filterParams,
    filtersOptions,
    isFilterOn,
    isFilterLoading,
    setSearchParams,
    sortIcon,
    showFilters,
    setShowFilters,
    catalogView,
    setCatalogView,
    setIsLoading
  } = useCatalogLogic(isOwnEvents, contextData, isInfinityList, configuration);

  const [filtersConfig] = useState(customerLoyaltySorters(isOwnEvents, contextData));
  const [btnDisabled, setBtnDisabled] = useState(false);

  const eventItemConfig = isOwnEvents
    ? configuration?.MyAreaCustomerLoyaltyEventItem
    : configuration?.MainCustomerLoyaltyEventItem;

  const isMobile = useMediaQuery('(max-width:600px)');

  const handleView = () => {
    setIsLoading(true);
    setCatalogView((prev) => (prev === 'grid' ? 'list' : 'grid'));
  };

  const handlePageChange = (evt, page) => {
    setCurrentPage(page);
    if (typeof setSearchParams === 'function') {
      setSearchParams((prev) => ({ ...prev, page: page - 1 }));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const loadMore = () => {
    if (totalPages > currentPage) {
      setCurrentPage((prevPageNumber) => prevPageNumber + 1);
      setSearchParams((prev) => ({ ...prev, page: currentPage }));
      if (totalPages === currentPage + 1) {
        setBtnDisabled(true);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <StyledDrawer anchor='left' open={showFilters} onClose={() => setShowFilters(false)}>
        <Box sx={{ padding: 2 }}>
          {/* Filters Component */}
          <CatalogFilters
            configFilters={availableFilterOptions}
            filterParams={filterParams}
            onFilterChange={onFilterChange}
            setShowFilters={setShowFilters}
            isFilterLoading={isFilterLoading}
            onApplyFilters={() => {
              setShowFilters(false);
              onApplyFilters();
            }}
            onResetFilters={() => {
              setShowFilters(false);
              onResetFilters();
            }}
            filtersOptions={filtersOptions}
          />
        </Box>
      </StyledDrawer>
      <StyledContainer component='main'>
        <Grid container spacing={2} alignItems='center' sx={{ marginTop: 1 }}>
          <Grid
            item
            xs={isMobile ? 4 : 2}
            md={1}
            sx={{
              textAlign: 'left',
              display: 'flex'
            }}
          >
            <FilterButton
              setShowFilters={setShowFilters}
              isFilterOn={isFilterOn}
              showFilters={showFilters}
            />
            <Sorters
              configuration={filtersConfig}
              sortBy={searchParams?.sortBy}
              sortDirection={searchParams?.sortDirection}
              handleSorting={handleSorting}
              isMobile={isMobile}
              sortIcon={sortIcon}
            />
          </Grid>
          <Grid item xs={8} md={10}>
            <StyledInputBase
              fullWidth
              placeholder={Localize.get('Catalog.search')}
              startAdornment={<Search sx={{ marginRight: 1 }} />}
              onKeyUp={(e) => onSearchFieldChange(e)}
            />
          </Grid>
          {!isMobile && (
            <Grid item xs={2} md={1} sx={{ textAlign: 'left' }}>
              <StyledIconButton
                onClick={handleView}
                aria-label={
                  catalogView === 'grid'
                    ? Localize.get('Catalog.switchToListView')
                    : Localize.get('Catalog.switchToGridView')
                }
              >
                {catalogView === 'grid' ? <StyledViewModule /> : <StyleViewList />}
              </StyledIconButton>
            </Grid>
          )}
        </Grid>

        {/* Loader */}
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '60vh'
            }}
          >
            <CircularProgress size={60} />
          </Box>
        ) : (
          <Box sx={{ marginTop: 2 }}>
            {events?.length ? (
              <Grid container spacing={2} sx={{ minHeight: '80vh', alignContent: 'start' }}>
                {events.map((event) => (
                  <EventItem
                    key={event.id}
                    event={event}
                    itemConfig={eventItemConfig}
                    view={catalogView}
                    isInfinityList={isInfinityList}
                    onItemClick={onItemClick}
                  />
                ))}
              </Grid>
            ) : (
              <StyledBox>
                <Typography>{Localize.get('Catalog.noEvents')}</Typography>
              </StyledBox>
            )}

            {/* Pagination */}
            <Stack
              alignItems='center'
              sx={{
                marginTop: 1,
                display: 'flex',
                justifyContent: 'center',
                position: !isInfinityList ? 'relative' : 'inherit',
                minHeight: '2.5rem'
              }}
            >
              {!isInfinityList ? (
                <Box sx={{ position: 'absolute', bottom: { xs: '-0.5em', sm: '-0.6em' } }}>
                  {totalPages > 1 && (
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(event, page) => handlePageChange(event, page)}
                      sx={{ marginBottom: 1.2 }}
                    />
                  )}
                </Box>
              ) : (
                <Box sx={{ position: 'absolute' }}>
                  <Button disabled={btnDisabled} onClick={loadMore}>
                    {Localize.get('Catalog.loadMore')}
                  </Button>
                </Box>
              )}
            </Stack>
          </Box>
        )}
      </StyledContainer>
    </>
  );
};

export default Catalog;
